import { AuthServiceInterface } from '@freelancer/auth';
import { DatastoreInterface, LOGGED_OUT_KEY } from '@freelancer/datastore/core';
import { DatastoreTestingController } from '@freelancer/datastore/testing';
import { toNumber } from '@freelancer/utils';
import { map, switchMap, take } from 'rxjs/operators';
import {
  printDatastoreState,
  setAuth,
  setDatastoreController,
} from './document-creators';
import { constructInitialStoreState } from './initial-states';
import { addMutationPropagators } from './mutation-propagators';
import { addPushTransformers } from './push-transformers';

export function datastoreInitializer(
  { isTesting } = { isTesting: false },
): (
  auth: AuthServiceInterface,
  datastore: DatastoreInterface,
  datastoreController: DatastoreTestingController,
) => Promise<void> {
  return (
    auth: AuthServiceInterface,
    datastore: DatastoreInterface,
    datastoreController: DatastoreTestingController,
  ) => {
    setAuth(auth);
    setDatastoreController(datastoreController);

    const userId$ = auth.authState$.pipe(
      map(authState => toNumber(authState ? authState.userId : LOGGED_OUT_KEY)),
    );

    /**
     * These are done whenever someone loads a page OR logs in for the first time.
     *
     * This allows you to log in as a custom user and have your state set up automatically,
     * but still maintain your state if you log out and back in.
     */
    if (!isTesting) {
      userId$
        .pipe(
          switchMap(authUid =>
            datastoreController
              .isStateEmpty()
              .then(isStateEmpty => ({ authUid, isStateEmpty })),
          ),
        )
        .subscribe(({ authUid, isStateEmpty }) => {
          if (isStateEmpty) {
            constructInitialStoreState(auth, datastore, authUid);
            printDatastoreState();
          }
        });
    }

    // These are only done once
    return userId$
      .pipe(take(1))
      .toPromise()
      .then(authUid => {
        addPushTransformers(authUid);
        addMutationPropagators(authUid);
      })
      .catch(err => console.error('Failed to initialize datastore:', err));
  };
}
